import React, {useState} from 'react';
import {curry} from 'ramda';
import wrap from 'wraptile';
import {OnStringSetState} from '@pluto/@types';
import DeleteDialog from '@pluto/delete-dialog';
import {TimelineMarker, TimelineRowType} from 'VideoAudioQC/Timeline/TimelineTypes';
import {ToggleSelectedMarker} from 'VideoAudioQC/Timeline/Redux/TimelineActions';
import {SetAdPreview} from 'VideoAudioQC/AccuratePlayer/Redux/AccuratePlayerActions';
import {onAdBreakNoteZoomIn} from 'VideoAudioQC/Timeline/TimelineUtils';
import {
    RemoveAdBreaksMarkerAction,
    UpdateAdBreaksMarkerAction,
} from 'VideoAudioQC/Timeline/AdBreaks/Redux/AdBreaksActions';
import {Item} from 'VideoAudioQC/QCMedia/Redux/QCMediaActions';
import AdBreaks from './AdBreaks';
import {AdBreakData} from './Table/Table';
import {OnEditMarkerProps} from '../../VideoAudioQCContainer';

import './AdBreaksContainer.scss';

export interface AdBreaksContainerProps {
    item: Item;
    identifier: string;
    markers: TimelineMarker[];
    selectedRowId: string;
    selectedMarkerId: string;
    adOnPlayingFrame?: TimelineMarker;
    onCreateMarker: (rowId: string) => void;
    onEditMarker: (props: OnEditMarkerProps) => void;
    onGoToMarker: (frame: number) => Promise<void>;
    toggleSelectedMarker: ToggleSelectedMarker;
    previewAdBreak: SetAdPreview;
    removeAdBreaksMarker: RemoveAdBreaksMarkerAction;
    updateAdBreaksMarker: UpdateAdBreaksMarkerAction;
    allotment: number | null;
    contentType: string;
    jobDeliveryDate: string;
}

export const onMarkerClick = curry(
    (
        props: AdBreaksContainerProps,
        onAdBreakNoteZoomIn: (marker: AdBreakData) => void,
        marker: AdBreakData
    ) => {
        const {identifier, toggleSelectedMarker} = props;

        toggleSelectedMarker(marker.id, identifier, TimelineRowType.AdBreak);
        onAdBreakNoteZoomIn(marker);
    }
);

export const onEditMarkerClick = curry(
    (props: AdBreaksContainerProps, item: AdBreakData) => {
        const {identifier, selectedMarkerId, onEditMarker, toggleSelectedMarker} = props;
        const {id, start} = item;

        if (selectedMarkerId !== id) {
            toggleSelectedMarker(id, identifier, TimelineRowType.AdBreak);
        }

        const selectedMarker: TimelineMarker = {
            identifier: id,
            start,
            end: start + 1,
        };

        onEditMarker({
            selectedMarker,
            selectedType: TimelineRowType.AdBreak,
        });
    }
);

export const seekAndSelect = async (props: AdBreaksContainerProps, item: AdBreakData) => {
    const {identifier, selectedMarkerId, onGoToMarker, toggleSelectedMarker} = props;
    const {id, start} = item;

    await onGoToMarker(start);

    if (selectedMarkerId !== id)
        toggleSelectedMarker(id, identifier, TimelineRowType.AdBreak);
};

export const onGoToMarkerClick = curry(
    (props: AdBreaksContainerProps, item: AdBreakData) => {
        seekAndSelect(props, item);
    }
);

export const onPreviewAdClick = curry(
    (props: AdBreaksContainerProps, item: AdBreakData) => {
        const {previewAdBreak} = props;
        const {start} = item;

        previewAdBreak([
            {
                frame: start,
            },
        ]);
    }
);

export const markerToFrameObject = (marker: TimelineMarker) => {
    return {
        frame: marker.start,
    };
};

export const onPreviewAllAdClick = wrap((props: AdBreaksContainerProps) => {
    const {markers, previewAdBreak} = props;

    const frames = markers.map(markerToFrameObject);

    previewAdBreak(frames);
});

export const onCreateMarkerClick = wrap((props: AdBreaksContainerProps) => {
    const {identifier, onCreateMarker} = props;

    onCreateMarker(identifier);
});

export const onShowDeleteDialog = curry(
    (setDeleteId: OnStringSetState, item: AdBreakData) => {
        const {id} = item;

        setDeleteId(id);
    }
);

export type DeleteDialogProps = {
    item: Item;
    deleteId: string;
    setDeleteId: OnStringSetState;
    removeAdBreaksMarker: RemoveAdBreaksMarkerAction;
};

export const onCloseDialog = curry((props: DeleteDialogProps, confirm: boolean) => {
    const {item, deleteId, setDeleteId, removeAdBreaksMarker} = props;
    const {adBreaks} = item;

    if (confirm) {
        removeAdBreaksMarker(adBreaks, deleteId);
    }

    setDeleteId('');
});

export const titleText = 'Are you sure you want to delete the ad break?';

export const getDeleteDialog = (props: DeleteDialogProps) => {
    const {deleteId} = props;
    const handleClose = onCloseDialog(props);

    if (!deleteId || deleteId.length === 0) return null;

    return <DeleteDialog text={titleText} onCloseDialog={handleClose} />;
};

export const onStartFrameChange = wrap(
    (props: AdBreaksContainerProps, data: AdBreakData, start: number) => {
        const {updateAdBreaksMarker, item} = props;
        const {adBreaks} = item;
        const {duration} = item.video;
        const {id} = data;

        const marker: TimelineMarker = {
            identifier: id,
            start,
            end: start + 1,
        };

        updateAdBreaksMarker(adBreaks, duration, marker);
    }
);

export const onClickOutsideList = wrap((props: AdBreaksContainerProps) => {
    const {identifier, toggleSelectedMarker, selectedMarkerId} = props;

    if (!selectedMarkerId) return;

    // Deselect selected marker
    toggleSelectedMarker(selectedMarkerId, identifier, TimelineRowType.AdBreak);
});

export default function AdBreaksContainer(props: AdBreaksContainerProps) {
    const {
        markers,
        selectedMarkerId,
        adOnPlayingFrame,
        item,
        removeAdBreaksMarker,
        allotment,
        contentType,
        jobDeliveryDate,
    } = props;

    const videoData = {
        dropFrame: item.video.dropFrame,
        frameRateValue: item.video.frameRate.numerator / item.video.frameRate.denominator,
    };

    const [deleteId, setDeleteId] = useState<string>('');
    const handleMarkerClick = onMarkerClick(props, onAdBreakNoteZoomIn);
    const handleEditMarkerClick = onEditMarkerClick(props);
    const handleGoToMarkerClick = onGoToMarkerClick(props);
    const handlePreviewAdClick = onPreviewAdClick(props);
    const handlePreviewAllAdClick = onPreviewAllAdClick(props);
    const handleCreateMarkerClick = onCreateMarkerClick(props);
    const handleShowDeleteDialog = onShowDeleteDialog(setDeleteId);

    const deleteDialog = getDeleteDialog({
        item,
        deleteId,
        setDeleteId,
        removeAdBreaksMarker,
    });
    const handleStartFrameChange = onStartFrameChange(props);

    const handleOnClickOutsideList = onClickOutsideList(props);

    return (
        <div className="ad-breaks-table-container">
            <AdBreaks
                item={item}
                jobDeliveryDate={jobDeliveryDate}
                allotment={allotment}
                videoData={videoData}
                markers={markers}
                contentType={contentType}
                selectedMarkerId={selectedMarkerId}
                adOnPlayingFrame={adOnPlayingFrame}
                onCreateMarker={handleCreateMarkerClick}
                onMarkerClick={handleMarkerClick}
                onEditMarkerClick={handleEditMarkerClick}
                onGoToMarkerClick={handleGoToMarkerClick}
                onDeleteClick={handleShowDeleteDialog}
                onPreviewAdClick={handlePreviewAdClick}
                onPreviewAllAdClick={handlePreviewAllAdClick}
                onStartFrameChange={handleStartFrameChange}
                onClickOutsideList={handleOnClickOutsideList}
            />
            {deleteDialog}
        </div>
    );
}
